import { useState } from 'react';
import { useBulletinList, useDeleteBulletin } from '@/hooks/useBulletin';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BulkSelectionToolbar } from '@/components/list/BulkSelectionToolbar';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import {
  Search,
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  ChevronLeft,
  ChevronRight,
  Calendar,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Bulletin } from '@/types';

export function BulletinList() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([]);

  const { data, isLoading } = useBulletinList({ page, limit: 10, search });
  const deleteBulletin = useDeleteBulletin();
  const selection = useBulkSelection((data?.data || []).map((item) => item.id));

  const handleHapus = async () => {
    if (pendingDeleteIds.length) {
      for (const id of pendingDeleteIds) {
        await deleteBulletin.mutateAsync(id);
      }
      selection.clearSelection();
      setPendingDeleteIds([]);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Postingan</h2>
          <p className="text-muted-foreground">
            Kelola postingan untuk halaman publik.
          </p>
        </div>
        <Link to="/postingan/create">
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Buat Postingan
          </Button>
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cari postingan..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <BulkSelectionToolbar
            className="m-4 mb-0"
            selectedCount={selection.selectedCount}
            itemLabel="postingan"
            onClear={selection.clearSelection}
            onDelete={() => setPendingDeleteIds(selection.selectedIds)}
            deleteDisabled={deleteBulletin.isPending}
          />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox checked={selection.allSelected} onCheckedChange={(checked) => selection.toggleAll(Boolean(checked))} />
                </TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Kode</TableHead>
                <TableHead>Nama</TableHead>
                <TableHead>Edisi</TableHead>
                <TableHead>Tag</TableHead>
                <TableHead>Tanggal</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    Memuat...
                  </TableCell>
                </TableRow>
              ) : data?.data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    Belum ada postingan.
                  </TableCell>
                </TableRow>
              ) : (
                data?.data.map((bulletin: Bulletin) => (
                  <TableRow key={bulletin.id} className={selection.isSelected(bulletin.id) ? 'bg-muted/30' : undefined}>
                    <TableCell>
                      <Checkbox checked={selection.isSelected(bulletin.id)} onCheckedChange={(checked) => selection.toggleOne(bulletin.id, Boolean(checked))} />
                    </TableCell>
                    <TableCell>{bulletin.id}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{bulletin.kode || '-'}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{bulletin.nama}</TableCell>
                    <TableCell>{bulletin.edisi || '-'}</TableCell>
                    <TableCell>
                      {bulletin.tag ? (
                        <Badge variant="secondary">{bulletin.tag}</Badge>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {bulletin.tanggal ? (
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          {new Date(bulletin.tanggal).toLocaleDateString('id-ID')}
                        </div>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/postingan/${bulletin.id}/edit`}>
                              <Eye className="mr-2 h-4 w-4" />
                              Lihat
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to={`/postingan/${bulletin.id}/edit`}>
                              <Edit className="mr-2 h-4 w-4" />
                              Edit
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setPendingDeleteIds([bulletin.id])}
                            className="text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm text-muted-foreground">
          Halaman {page} dari {Math.ceil((data?.total || 0) / 10)}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage((p) => p + 1)}
          disabled={page >= Math.ceil((data?.total || 0) / 10)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <Dialog open={pendingDeleteIds.length > 0} onOpenChange={() => setPendingDeleteIds([])}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Yakin ingin melanjutkan?</DialogTitle>
            <DialogDescription>
              Tindakan ini tidak dapat dibatalkan. This will permanently delete the
              postingan beserta seluruh artikel terkait.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDeleteIds([])}>
              Batal
            </Button>
            <Button variant="destructive" onClick={handleHapus}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
